"use client";

import { c as _c } from "react/compiler-runtime";
import { filesControllerFindByScopeIdQueryOptions, foldersControllerFindAllQueryOptions, type HierarchiesControllerGetDescendantsQueryParamsScopeTypeEnumKey, hierarchiesControllerGetDescendantsQueryParamsExcludeFileStatusesEnum, useHierarchiesControllerGetDescendants, useTeamsControllerFindProjectsInTeam } from "@dc/gateway-jsonapi";
import { useDebounce } from "@dc/ui/hooks/useDebounce";
import { useQueries } from "@tanstack/react-query";
import { matchSorter } from "match-sorter";
import { usePylonArticles } from "@/hooks/usePylonArticles";
import { useTeamsContext } from "@/hooks/useTeamsContext";
import { useOrgTeamPath } from "@/lib/orgTeamPath";
import type { SearchResultItem } from "./GlobalSearch.types";
import { useNavigationSearchItems } from "./useNavigationSearchItems";
const DEBOUNCE_MS = 300;
const MIN_API_QUERY_LENGTH = 2;
const MAX_RESULTS_PER_CATEGORY = 5;
const MAX_NAVIGATION_RESULTS = 8;

/** IDs of navigation items to show as Quick Links when query is empty */
const QUICK_LINK_IDS = new Set(["settings-users", "settings-teams", "settings-general"]);
const FEATURED_ARTICLE_IDS = new Set(["a6421c40-fde3-4e93-8a59-9520296d0bc0",
// How to Login and Register
"92cef159-9d37-4cd2-875d-12e8b956ad96",
// How to Create Projects
"ea90c2b5-72f0-4025-8c0f-dd886467e861",
// Document Upload Flow
"341f63ab-2f8d-4885-9e85-1792a5baca70",
// Using Document Crunch's Chat Feature
"ea031d8b-83f8-4b20-ad8a-01e4c5b67e4d" // Understanding Notifications
]);
const FILE_EXCLUDED_STATUSES = [hierarchiesControllerGetDescendantsQueryParamsExcludeFileStatusesEnum.PENDING, hierarchiesControllerGetDescendantsQueryParamsExcludeFileStatusesEnum.UPLOADING, hierarchiesControllerGetDescendantsQueryParamsExcludeFileStatusesEnum.UPLOADED, hierarchiesControllerGetDescendantsQueryParamsExcludeFileStatusesEnum.PROCESSING, hierarchiesControllerGetDescendantsQueryParamsExcludeFileStatusesEnum.VERSIONED, hierarchiesControllerGetDescendantsQueryParamsExcludeFileStatusesEnum.FAILED];
function buildProjectHref(projectId: string, scopeId?: string, scopeType?: string) {
  const params = new URLSearchParams();
  if (scopeId) params.set("scopeId", scopeId);
  if (scopeType) params.set("scopeType", scopeType);
  const search = params.toString();
  return search ? `/files/projects/${projectId}?${search}` : `/files/projects/${projectId}`;
}
function toArticleItem(a: {
  id: string;
  identifier: string;
  title: string;
  url: string;
}): SearchResultItem {
  return {
    id: a.identifier,
    category: "articles",
    title: a.title,
    href: a.url
  };
}
export function useGlobalSearchQuery(query, enabled) {
  const $ = _c(41);
  const debouncedQuery = useDebounce(query, DEBOUNCE_MS);
  const {
    build
  } = useOrgTeamPath();
  const {
    currentTeamId,
    currentScopeId
  } = useTeamsContext();
  const navigationItems = useNavigationSearchItems();
  const shouldFetchApi = debouncedQuery.length >= MIN_API_QUERY_LENGTH;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      size: MAX_RESULTS_PER_CATEGORY
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== debouncedQuery) {
    t1 = {
      search: debouncedQuery,
      page: t0
    };
    $[1] = debouncedQuery;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = shouldFetchApi && !!currentTeamId;
  let t3;
  if ($[3] !== t2) {
    t3 = {
      query: {
        enabled: t2
      }
    };
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    data: projectsData,
    isLoading: isLoadingProjects
  } = useTeamsControllerFindProjectsInTeam(currentTeamId ?? "", t1, t3);
  let t4;
  if ($[5] !== debouncedQuery) {
    t4 = {
      search: debouncedQuery,
      scopeType: "FOLDER" as HierarchiesControllerGetDescendantsQueryParamsScopeTypeEnumKey,
      pageSize: MAX_RESULTS_PER_CATEGORY
    };
    $[5] = debouncedQuery;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = shouldFetchApi && !!currentScopeId;
  let t6;
  if ($[7] !== t5) {
    t6 = {
      query: {
        enabled: t5
      }
    };
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const {
    data: folderDescendantsData,
    isLoading: isLoadingFolderDescendants
  } = useHierarchiesControllerGetDescendants(currentScopeId ?? "", t4, t6);
  const folderScopeIds = (folderDescendantsData?.data ?? []).slice(0, MAX_RESULTS_PER_CATEGORY).map(_temp);
  const folderQueries = useQueries({
    queries: folderScopeIds.map(_temp2)
  });
  let t7;
  if ($[9] !== debouncedQuery) {
    t7 = {
      search: debouncedQuery,
      scopeType: "FILE" as HierarchiesControllerGetDescendantsQueryParamsScopeTypeEnumKey,
      pageSize: MAX_RESULTS_PER_CATEGORY,
      excludeFileStatuses: FILE_EXCLUDED_STATUSES
    };
    $[9] = debouncedQuery;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = shouldFetchApi && !!currentScopeId;
  let t9;
  if ($[11] !== t8) {
    t9 = {
      query: {
        enabled: t8
      }
    };
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const {
    data: descendantsData,
    isLoading: isLoadingDescendants
  } = useHierarchiesControllerGetDescendants(currentScopeId ?? "", t7, t9);
  let t10;
  let t11;
  if ($[13] !== descendantsData?.data) {
    const fileScopeIds = (descendantsData?.data ?? []).slice(0, MAX_RESULTS_PER_CATEGORY).map(_temp3);
    t11 = useQueries;
    t10 = fileScopeIds.map(_temp4);
    $[13] = descendantsData?.data;
    $[14] = t10;
    $[15] = t11;
  } else {
    t10 = $[14];
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t10) {
    t12 = {
      queries: t10
    };
    $[16] = t10;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const fileQueries = t11(t12);
  let t13;
  if ($[18] !== navigationItems || $[19] !== query) {
    t13 = query.length === 0 ? navigationItems.filter(_temp5) : matchSorter(navigationItems, query, {
      keys: ["title", "subtitle", {
        key: "keywords",
        threshold: matchSorter.rankings.CONTAINS
      }]
    }).slice(0, MAX_NAVIGATION_RESULTS);
    $[18] = navigationItems;
    $[19] = query;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const navigation = t13;
  const searchLower = debouncedQuery.toLowerCase();
  const projects = !projectsData?.data ? [] : projectsData.data.filter(item_2 => {
    const name = item_2.attributes.name ?? "";
    return name.toLowerCase().includes(searchLower);
  }).slice(0, MAX_RESULTS_PER_CATEGORY).map(item_3 => ({
    id: item_3.id,
    category: "projects" as const,
    title: item_3.attributes.name ?? "Untitled Project",
    subtitle: item_3.attributes.location ?? undefined,
    href: build(buildProjectHref(item_3.id, item_3.relationships?.scope?.data?.id, "PROJECT"))
  }));
  let t14;
  if ($[21] !== folderQueries || $[22] !== isLoadingFolderDescendants) {
    t14 = isLoadingFolderDescendants || folderQueries.some(_temp6);
    $[21] = folderQueries;
    $[22] = isLoadingFolderDescendants;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const isLoadingFolders = t14;
  const folders = folderScopeIds.reduce((acc, scopeId_1, i) => {
    const folderData = folderQueries[i]?.data;
    const folder = folderData?.data?.[0];
    const name_0 = folder?.attributes.name;
    const scope = (folderData?.included as {
      id: string;
      type: string;
      attributes?: {
        path?: string;
      };
      relationships?: {
        project?: {
          data?: {
            id: string;
          } | null;
        };
      };
    }[] | undefined)?.find(_temp7);
    const scopePath = scope?.attributes?.path;
    const projectId = scopePath?.match(/\/project\/([^/]+)/)?.[1];
    if (folder && name_0 && name_0.toLowerCase().includes(searchLower) && projectId) {
      acc.push({
        id: scopeId_1,
        category: "folders",
        title: name_0,
        href: build(buildProjectHref(projectId, scopeId_1, "FOLDER"))
      });
    }
    return acc;
  }, []);
  let t15;
  if ($[24] !== fileQueries || $[25] !== isLoadingDescendants) {
    t15 = isLoadingDescendants || fileQueries.some(_temp8);
    $[24] = fileQueries;
    $[25] = isLoadingDescendants;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  const isLoadingFiles = t15;
  const files = fileQueries.reduce((acc_0, q_1) => {
    const file = q_1.data?.data;
    const name_1 = file?.attributes?.fileName ?? file?.attributes?.originalName;
    if (file?.id && name_1 && name_1.toLowerCase().includes(searchLower)) {
      acc_0.push({
        id: file.id,
        category: "files",
        title: name_1,
        href: build(`/crunches/${file.id}`)
      });
    }
    return acc_0;
  }, []);
  const {
    data: articlesData,
    isLoading: isLoadingArticles
  } = usePylonArticles(enabled);
  let articles;
  let t16;
  if ($[27] !== articlesData?.data || $[28] !== query) {
    const allArticles = articlesData?.data ?? [];
    articles = query.length < MIN_API_QUERY_LENGTH ? [] : matchSorter(allArticles, query, {
      keys: ["title"],
      threshold: matchSorter.rankings.CONTAINS
    }).slice(0, MAX_RESULTS_PER_CATEGORY).map(toArticleItem);
    t16 = allArticles.filter(_temp9).map(toArticleItem);
    $[27] = articlesData?.data;
    $[28] = query;
    $[29] = articles;
    $[30] = t16;
  } else {
    articles = $[29];
    t16 = $[30];
  }
  const featuredArticles = t16;
  const isLoading = shouldFetchApi && (isLoadingProjects || isLoadingFolders || isLoadingFiles);
  const hasResults = navigation.length > 0 || projects.length > 0 || folders.length > 0 || files.length > 0 || articles.length > 0;
  let t17;
  if ($[31] !== articles || $[32] !== featuredArticles || $[33] !== files || $[34] !== folders || $[35] !== hasResults || $[36] !== isLoading || $[37] !== isLoadingArticles || $[38] !== navigation || $[39] !== projects) {
    t17 = {
      navigation,
      projects,
      folders,
      files,
      articles,
      featuredArticles,
      isLoading,
      isLoadingArticles,
      hasResults
    };
    $[31] = articles;
    $[32] = featuredArticles;
    $[33] = files;
    $[34] = folders;
    $[35] = hasResults;
    $[36] = isLoading;
    $[37] = isLoadingArticles;
    $[38] = navigation;
    $[39] = projects;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  return t17;
}
function _temp9(a) {
  return FEATURED_ARTICLE_IDS.has(a.id);
}
function _temp8(q_0) {
  return q_0.isLoading;
}
function _temp7(inc) {
  return inc.type === "scopes";
}
function _temp6(q) {
  return q.isLoading;
}
function _temp5(item_1) {
  return QUICK_LINK_IDS.has(item_1.id);
}
function _temp4(scopeId_0) {
  return {
    ...filesControllerFindByScopeIdQueryOptions(scopeId_0),
    staleTime: 300000
  };
}
function _temp3(item_0) {
  return item_0.id;
}
function _temp2(scopeId) {
  return {
    ...foldersControllerFindAllQueryOptions({
      filter: {
        scopeId
      },
      include: "scope.project"
    }),
    staleTime: 300000
  };
}
function _temp(item) {
  return item.id;
}